import React from "react";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { CategoryResult } from "@/types/supabase";
import { toast } from "sonner";
import { initiateCheckout as initiateUpgrade } from "@/hooks/useSubscription";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { ListingFormValues } from "./types";
import { categoryPillClassName, categoryPillRemoveClassName } from "@/components/shared/selectionPillStyles";

interface BasicInfoSectionProps {
  form: UseFormReturn<ListingFormValues>;
  isDisabled: boolean;
  categories: CategoryResult[];
  selectedCategories: string[];
  handleCategorySelect: (category: string) => void;
  handleRemoveCategory: (category: string) => void;
  isPremium?: boolean;
  isAdmin?: boolean;
  isWhatsAppTier?: boolean;
}

export default function BasicInfoSection({ 
  form, 
  isDisabled, 
  categories,
  selectedCategories,
  handleCategorySelect,
  handleRemoveCategory,
  isPremium = false,
  isAdmin = false,
  isWhatsAppTier = false
}: BasicInfoSectionProps) {
  const canSelectMultipleCategories = isPremium || isAdmin;

  // Create a handler for the upgrade button click
  const handleUpgradeClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    initiateUpgrade(); // Internal upgrade flow
  };

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        <div>
          <h3 className="public-card-title">Categories</h3>
          <p className="mt-1 public-small">Choose how people should discover your service.</p>
        </div>

        <div className="flex items-center justify-between">
          <Label>Categories</Label>
          {!canSelectMultipleCategories && selectedCategories.length >= 1 && (
            <p className="text-sm text-amber-600">
              {isWhatsAppTier ? 
                "WhatsApp Verified includes one category." : 
                "Starter includes one category."}
              <Button 
                variant="link" 
                className="text-amber-700 h-auto p-0 ml-1"
                onClick={handleUpgradeClick}
              >
                Upgrade to Premium
              </Button>
            </p>
          )}
        </div>
        
        {selectedCategories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {selectedCategories.map((category) => (
              <Badge
                key={category}
                variant="secondary"
                className={categoryPillClassName}
              >
                {category}
                <X
                  className={`h-3 w-3 cursor-pointer ${categoryPillRemoveClassName}`}
                  onClick={() => handleRemoveCategory(category)}
                />
              </Badge>
            ))}
          </div>
        )}

        <Select
          disabled={isDisabled}
          onValueChange={(value) => {
            if (!selectedCategories.includes(value) && 
                (!canSelectMultipleCategories && selectedCategories.length >= 1)) {
              toast.error(isWhatsAppTier ? 
                "WhatsApp Verified includes one category per listing. Upgrade to Premium for broader category coverage." : 
                "Starter accounts include one category per listing. Upgrade to Premium for broader category coverage.");
              return;
            }
            handleCategorySelect(value);
          }}
          value=""
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a category" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((category) => (
              <SelectItem 
                key={category.name} 
                value={category.name}
                disabled={!canSelectMultipleCategories && selectedCategories.length >= 1 && !selectedCategories.includes(category.name)}
              >
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {form.formState.errors.categories?.message && (
          <p className="text-sm font-medium text-destructive">
            {String(form.formState.errors.categories.message)}
          </p>
        )}
      </div>
    </div>
  );
}
